import React, { createContext, useContext, useState } from 'react';

export interface DemoScenario {
  id: string;
  name: string;
  description: string;
  metrics: {
    agents: number;
    modules: number;
    alerts: number;
    efficiency: number;
  };
  events: string[];
}

// Named separately (rather than accessed via demoScenarios[0]) so DemoProvider's initial
// state below can reference a known-defined DemoScenario without an unsafe array index.
export const defaultDemoScenario: DemoScenario = {
  id: 'enterprise-ops',
  name: 'Enterprise Operations',
  description: 'High-load enterprise environment with multiple active projects',
  metrics: {
    agents: 47,
    modules: 85,
    alerts: 2,
    efficiency: 94
  },
  events: [
    'Security module deployment completed',
    'Security override by Admin-007',
    'TacticalAgent-T7 performance optimization',
    'New enterprise client onboarded'
  ]
};

export const demoScenarios: DemoScenario[] = [
  defaultDemoScenario,
  {
    id: 'crisis-simulation',
    name: 'Crisis Management',
    description: 'Simulated crisis scenario demonstrating emergency protocols',
    metrics: {
      agents: 32,
      modules: 67,
      alerts: 8,
      efficiency: 78
    },
    events: [
      'CRITICAL: Security breach detected',
      'Alpha persona emergency override activated',
      'Automatic failover to backup systems',
      'Crisis response team notified'
    ]
  },
  {
    id: 'growth-showcase',
    name: 'Rapid Scale Demo',
    description: 'Demonstrates platform scaling from startup to enterprise',
    metrics: {
      agents: 15,
      modules: 28,
      alerts: 0,
      efficiency: 87
    },
    events: [
      'Startup team onboarding complete',
      'First automation workflow deployed',
      'ROI targets exceeded by 120%',
      'Scaling to Professional tier'
    ]
  }
];

interface DemoContextValue {
  isDemoMode: boolean;
  activeScenario: DemoScenario;
  scenarios: DemoScenario[];
  setIsDemoMode: (value: boolean) => void;
  setActiveScenario: (scenario: DemoScenario) => void;
}

const DemoContext = createContext<DemoContextValue | null>(null);

interface DemoProviderProps {
  children: React.ReactNode;
  initialState?: boolean;
}

export function DemoProvider({ children, initialState = false }: DemoProviderProps) {
  const [isDemoMode, setIsDemoMode] = useState(initialState);
  const [activeScenario, setActiveScenario] = useState<DemoScenario>(defaultDemoScenario);

  const value: DemoContextValue = {
    isDemoMode,
    activeScenario,
    scenarios: demoScenarios,
    setIsDemoMode,
    setActiveScenario,
  };

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
}

export function useDemoMode() {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemoMode must be used within a DemoProvider');
  }
  return {
    isDemoMode: context.isDemoMode,
    activeScenario: context.activeScenario,
    scenarios: context.scenarios,
    setIsDemoMode: context.setIsDemoMode,
    setActiveScenario: context.setActiveScenario,
  };
}
